// constants/images.ts
import type { EventType } from '@/constants/geo';

type Mode = 'dark' | 'light';
type DeviceSize = 'mobile' | 'desktop';
type BackgroundImageSet = Record<Mode, Record<DeviceSize, string[]>>;

const hennaBaseUrl =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImageHenna';

const weddingBaseUrl =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImageWedding';

const backgroundImageVersion = '20260618-1';
const localBackgroundImageVersion = '20260615-1';
const hennaOptimizedMobileDarkHero = `${hennaBaseUrl}/mobileDark1-768.webp`;
const hennaOptimizedMobileDark3 = '/images/backgrounds/henna/mobileDark3-q78.webp';
const hennaOptimizedMobileDark4 = '/images/backgrounds/henna/mobileDark4-q78.webp';

const withBackgroundVersion = (url: string): string => {
  const version = url.startsWith('/images/backgrounds/')
    ? localBackgroundImageVersion
    : backgroundImageVersion;

  return `${url}${url.includes('?') ? '&' : '?'}v=${version}`;
};

const getHennaMobileDarkImage = (index: number): string => {
  if (index === 0) return hennaOptimizedMobileDarkHero;
  if (index === 2) return hennaOptimizedMobileDark3;
  if (index === 3) return hennaOptimizedMobileDark4;

  return `${hennaBaseUrl}/mobileDark${index + 1}.webp`;
};

const defaultBackgroundImages: BackgroundImageSet = {
  dark: {
    mobile: [
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection12.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection13.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection11.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection13.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileDarkSection11.webp',
    ],
    desktop: [
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection11.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection12.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection13.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection11.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection12.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopDarkSection13.webp',
    ],
  },
  light: {
    mobile: [
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection6.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection12.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection14.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection12.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/mobileLightSection14.webp',

    ],
    desktop: [
     // 'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/new4.png',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection7.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection8.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection9.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection10.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection8.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection9.webp',
      'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/backgroundImage/desktopLightSection10.webp',
    ],
  },
};

const cloneBackgroundImages = (images: BackgroundImageSet): BackgroundImageSet => ({
  dark: {
    mobile: images.dark.mobile.map(withBackgroundVersion),
    desktop: images.dark.desktop.map(withBackgroundVersion),
  },
  light: {
    mobile: images.light.mobile.map(withBackgroundVersion),
    desktop: images.light.desktop.map(withBackgroundVersion),
  },
});

const hennaBackgroundImages: BackgroundImageSet = {
  dark: {
    mobile: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(getHennaMobileDarkImage(index))
    ),
    desktop: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(`${hennaBaseUrl}/desktopDark${index + 1}.webp`)
    ),
  },
  light: {
    mobile: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(`${hennaBaseUrl}/mobileLight${index + 1}.webp`)
    ),
    desktop: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(`${hennaBaseUrl}/desktopLight${index + 1}.webp`)
    ),
  },
};

const weddingBackgroundImages: BackgroundImageSet = {
  dark: {
    mobile: Array.from(
      { length: 7 },
      (_, index) =>
        withBackgroundVersion(
          `${weddingBaseUrl}/mobileDark${index + 1}${index === 0 ? '-800' : ''}.webp`
        )
    ),
    desktop: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(`${weddingBaseUrl}/desktopDark${index + 1}.webp`)
    ),
  },
  light: {
    mobile: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(`${weddingBaseUrl}/mobileLight${index + 1}.webp`)
    ),
    desktop: Array.from(
      { length: 7 },
      (_, index) => withBackgroundVersion(`${weddingBaseUrl}/desktopLight${index + 1}.webp`)
    ),
  },
};

export const backgroundImagesByEvent: Record<EventType, BackgroundImageSet> = {
  'אירוע': weddingBackgroundImages,
  'חתונה': weddingBackgroundImages,
  'חינה': hennaBackgroundImages,
  'בר-מצווה': weddingBackgroundImages,
};

export const getBackgroundImagesForEvent = (
  eventType: EventType = 'אירוע'
): BackgroundImageSet => backgroundImagesByEvent[eventType] ?? cloneBackgroundImages(defaultBackgroundImages);

export const backgroundImages = cloneBackgroundImages(defaultBackgroundImages);
